import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
	ArrowLeft,
	Pencil,
	Sparkles,
	Trash2,
} from "lucide-react";
import type {
	ActionGroup,
	ActionGroupUpdateRequest,
	FilterRules,
} from "@/types/action-groups";
import { trackActionGroupFiltered } from "@/lib/analytics-events";
import { apiService } from "@/lib/api-service";
import { useSession } from "@/hooks/use-session";
import { useRole } from "@/hooks/use-role";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { GroupIcon } from "@/components/action-groups/group-icon";
import { FilterRuleChips } from "@/components/action-groups/filter-rule-chips";
import { PeopleTable } from "@/components/contacts/people-table";
import { VoterFileGroupMembersTable } from "@/components/action-groups/voter-file-group-members-table";
import { FilterRuleBuilder, isRuleComplete } from "@/components/action-groups/filter-rule-builder";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_app/_/action-groups/$groupId")({
	component: ActionGroupDetailPage,
});

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

function ActionGroupDetailPage() {
	const { groupId } = Route.useParams();
	const groupIdNum = Number(groupId);
	const navigate = useNavigate();
	const session = useSession();
	const campaignId = session.campaign?.id;
	const { isReadWrite } = useRole();

	const [editOpen, setEditOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);

	// Fetch group
	const {
		data: group,
		isLoading: groupLoading,
		error: groupError,
	} = useQuery({
		queryKey: ["action-groups", campaignId, groupIdNum],
		queryFn: () => apiService.getActionGroup(campaignId!, groupIdNum),
		enabled: !!campaignId,
	});

	if (!campaignId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl">
					<p className="text-muted-foreground">
						No campaign found. Please complete onboarding first.
					</p>
				</div>
			</div>
		);
	}

	if (groupLoading) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl flex items-center justify-center py-20">
					<Spinner className="size-6" />
				</div>
			</div>
		);
	}

	if (groupError || !group) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl space-y-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate({ to: "/action-groups" })}
					>
						<ArrowLeft data-icon="inline-start" className="size-4" />
						Back
					</Button>
					<div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
						<p className="text-destructive">
							Failed to load action group. It may have been deleted.
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-7xl space-y-4">
				{/* Header Bar */}
				<div className="flex items-center justify-between border-b pb-4">
					<div className="flex items-center gap-3">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => navigate({ to: "/action-groups" })}
						>
							<ArrowLeft data-icon="inline-start" className="size-4" />
							Back
						</Button>
						<div className="h-6 w-px bg-border" />
						<GroupIcon name={group.name} />
						<span className="text-base font-semibold">
							{group.name}
						</span>
						{group.source === "ai_suggested" && (
							<Sparkles className="size-4 text-purple-500" />
						)}
						<span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
							{group.member_count} {group.member_count === 1 ? "person" : "people"}
						</span>
					</div>
					<div className="flex items-center gap-2">
						{isReadWrite ? (
							<Button
								variant="outline"
								size="sm"
								onClick={() => setEditOpen(true)}
							>
								<Pencil data-icon="inline-start" className="size-4" />
								Edit
							</Button>
						) : (
							<Tooltip>
								<TooltipTrigger asChild>
									<span>
										<Button variant="outline" size="sm" disabled>
											<Pencil data-icon="inline-start" className="size-4" />
											Edit
										</Button>
									</span>
								</TooltipTrigger>
								<TooltipContent>Read-only users cannot perform this action</TooltipContent>
							</Tooltip>
						)}
						{isReadWrite ? (
							<Button
								variant="outline"
								size="sm"
								className="text-destructive hover:text-destructive"
								onClick={() => setDeleteOpen(true)}
							>
								<Trash2 data-icon="inline-start" className="size-4" />
								Delete
							</Button>
						) : (
							<Tooltip>
								<TooltipTrigger asChild>
									<span>
										<Button variant="outline" size="sm" className="text-destructive/50" disabled>
											<Trash2 data-icon="inline-start" className="size-4" />
											Delete
										</Button>
									</span>
								</TooltipTrigger>
								<TooltipContent>Read-only users cannot perform this action</TooltipContent>
							</Tooltip>
						)}
					</div>
				</div>

				{/* Filter Rule Chips */}
				<FilterRuleChips campaignId={campaignId} group={group} />

				{/* Members — VF groups query voter_file_records live; others
				    reuse PeopleTable which joins on ActionGroupMember. */}
				{group.group_type === "voter_file" ? (
					<VoterFileGroupMembersTable campaignId={campaignId} groupId={groupIdNum} />
				) : (
					<PeopleTable campaignId={campaignId} groupId={groupIdNum} />
				)}
			</div>

			{/* Edit Dialog */}
			<EditGroupDialog
				campaignId={campaignId}
				group={group}
				open={editOpen}
				onOpenChange={setEditOpen}
			/>

			{/* Delete Confirmation */}
			<DeleteGroupDialog
				campaignId={campaignId}
				group={group}
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
			/>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Edit Dialog
// ---------------------------------------------------------------------------

interface EditGroupDialogProps {
	campaignId: number;
	group: ActionGroup;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

function EditGroupDialog({
	campaignId,
	group,
	open,
	onOpenChange,
}: EditGroupDialogProps) {
	const queryClient = useQueryClient();

	const [name, setName] = useState(group.name);
	const [description, setDescription] = useState(group.description ?? "");
	const [filterRules, setFilterRules] = useState<FilterRules>(
		group.filter_rules ?? { logic: "AND", rules: [{ field: "", operator: "", value: "" }] },
	);

	// Reset form when dialog opens with fresh group data
	useEffect(() => {
		if (open) {
			setName(group.name);
			setDescription(group.description ?? "");
			setFilterRules(
				group.filter_rules ?? {
					logic: "AND",
					rules: [{ field: "", operator: "", value: "" }],
				},
			);
		}
	}, [open, group]);

	const updateMutation = useMutation({
		mutationFn: (data: ActionGroupUpdateRequest) =>
			apiService.updateActionGroup(campaignId, group.id, data),
		onSuccess: (_result, variables) => {
			if (
				(group.group_type === "dynamic" || group.group_type === "voter_file") &&
				variables.filter_rules
			) {
				trackActionGroupFiltered(
					variables.filter_rules.rules.filter((r) => r.field).length,
				);
			}
			queryClient.invalidateQueries({
				queryKey: ["action-groups", campaignId, group.id],
			});
			queryClient.invalidateQueries({
				queryKey: ["action-groups", campaignId],
			});
			onOpenChange(false);
		},
	});

	// PRD 41 §5.7 — isRuleComplete blocks save (not just warns). Previous
	// behavior silently dropped partial rules in handleSave, which let
	// users save a different filter than the one they thought they
	// configured.
	const isFilterGroup =
		group.group_type === "dynamic" || group.group_type === "voter_file";
	const hasIncompleteRule = filterRules.rules.some(
		(r) =>
			(r.field ||
				r.operator ||
				(r.value !== "" && r.value !== null && r.value !== undefined)) &&
			!isRuleComplete(r),
	);
	const validRules = filterRules.rules.filter(isRuleComplete);
	const filterRulesInvalid =
		isFilterGroup && (hasIncompleteRule || validRules.length === 0);

	function handleSave() {
		if (filterRulesInvalid) {
			return;
		}
		const payload: ActionGroupUpdateRequest = {
			name: name.trim(),
			description: description.trim() || undefined,
		};

		if (isFilterGroup && validRules.length > 0) {
			payload.filter_rules = { logic: "AND", rules: validRules };
		}

		updateMutation.mutate(payload);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>Edit Action Group</DialogTitle>
					<DialogDescription>
						Update the details of this action group.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="edit-name">Name</Label>
						<Input
							id="edit-name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							autoFocus
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="edit-description">
							Description{" "}
							<span className="font-normal text-muted-foreground">
								(optional)
							</span>
						</Label>
						<Textarea
							id="edit-description"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							rows={3}
						/>
					</div>

					{isFilterGroup && (
						<div className="space-y-2">
							<Label>Filter Rules</Label>
							<div className="max-h-[40vh] overflow-y-auto">
								<FilterRuleBuilder
									campaignId={campaignId}
									value={filterRules}
									onChange={setFilterRules}
									showPreview
									mode={group.group_type === "voter_file" ? "voter_file" : "contacts"}
								/>
							</div>
							{hasIncompleteRule && (
								<p className="text-sm text-destructive">
									Every rule needs a field, operator, and value before this group can be saved.
								</p>
							)}
							{!hasIncompleteRule && validRules.length === 0 && (
								<p className="text-sm text-muted-foreground">
									Add at least one complete rule to save this group.
								</p>
							)}
						</div>
					)}
				</div>

				{updateMutation.isError && (
					<p className="text-sm text-destructive">
						{updateMutation.error instanceof Error
							? updateMutation.error.message
							: "Failed to update group. Please try again."}
					</p>
				)}

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						disabled={
							!name.trim() ||
							updateMutation.isPending ||
							filterRulesInvalid
						}
					>
						{updateMutation.isPending ? (
							<>
								<Spinner className="size-4" />
								Saving...
							</>
						) : (
							"Save Changes"
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Delete Confirmation Dialog
// ---------------------------------------------------------------------------

interface DeleteGroupDialogProps {
	campaignId: number;
	group: ActionGroup;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

function DeleteGroupDialog({
	campaignId,
	group,
	open,
	onOpenChange,
}: DeleteGroupDialogProps) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const deleteMutation = useMutation({
		mutationFn: () => apiService.deleteActionGroup(campaignId, group.id),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["action-groups", campaignId],
			});
			onOpenChange(false);
			navigate({ to: "/action-groups" });
		},
	});

	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Delete Action Group</AlertDialogTitle>
					<AlertDialogDescription>
						Are you sure you want to delete "{group.name}"? This action
						cannot be undone and will remove all member associations.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={deleteMutation.isPending}>
						Cancel
					</AlertDialogCancel>
					<AlertDialogAction
						variant="destructive"
						onClick={() => deleteMutation.mutate()}
						disabled={deleteMutation.isPending}
					>
						{deleteMutation.isPending ? (
							<>
								<Spinner className="size-4" />
								Deleting...
							</>
						) : (
							"Delete"
						)}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
